import {
  OperatingRoomAgenda,
  OperatingRoomFilterDTO,
  OperatingRoomHeatCellDTO,
  OperatingRoomHeatMeasure,
  OperatingRoomNomenclatorDTO,
  OperatingRoomOptionDTO,
  OperatingRoomOutcome,
  OperatingRoomUrgency,
} from '../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { toISOString } from '../../utils/localeHelpers'

/**
 * Filtros de la pantalla «Indicadores de quirófano» que no son de columna: el período y los de «Más filtros». Viajan al
 * backend en `filters` (los mismos en los indicadores, cada pestaña, la lista y el export); los de columna de la barra
 * «+ Filtro» de la lista viajan aparte, en el `loadConfigDTO` de la grilla.
 */

/** Los filtros de «Más filtros» (`null` o vacío es «Todos»). El código de nomenclador se guarda con su nombre para el chip. */
export interface OperatingRoomSelectFilters {
  roomIds: number[]
  surgeryTypeId: number | null
  surgeonId: number | null
  nomenclator: OperatingRoomNomenclatorDTO | null
  financierId: number | null
  anesthesiaId: number | null
  agenda: OperatingRoomAgenda
  /** Un bloque de agenda: no es un combo, sale de «Ver cirugías» de una fila de «Agrupar por Agenda». Se ve como chip. */
  agendaBlock: OperatingRoomOptionDTO | null
  urgency: OperatingRoomUrgency
}

export type OperatingRoomFilterField = keyof OperatingRoomSelectFilters

/** Los filtros de «Más filtros», en el orden de la pantalla (y de los chips). */
export const OPERATING_ROOM_MORE_FILTERS: readonly OperatingRoomFilterField[] = [
  'roomIds', 'surgeryTypeId', 'surgeonId', 'nomenclator', 'financierId', 'anesthesiaId', 'agenda', 'agendaBlock', 'urgency',
]

/** Todos los filtros de «Más filtros» sin elegir. */
export const NO_SELECT_FILTERS: OperatingRoomSelectFilters = {
  roomIds: [],
  surgeryTypeId: null,
  surgeonId: null,
  nomenclator: null,
  financierId: null,
  anesthesiaId: null,
  agenda: 'ALL',
  agendaBlock: null,
  urgency: 'ALL',
}

/** Lo que hay en los filtros de la pantalla; las fechas pueden estar vacías mientras se escriben. */
export interface OperatingRoomFiltersState extends OperatingRoomSelectFilters {
  from: Date | null
  to: Date | null
}

const isoDay = (date: Date): string => toISOString(date)!.substring(0, 10)

/** Cuántos de los filtros de «Más filtros» hay elegidos (para «Más filtros (N)»). */
export function activeFilterCount(filters: OperatingRoomSelectFilters): number {
  return OPERATING_ROOM_MORE_FILTERS.filter((field) => isFilterActive(filters, field)).length
}

export function isFilterActive(filters: OperatingRoomSelectFilters, field: OperatingRoomFilterField): boolean {
  if (field === 'roomIds') return filters.roomIds.length > 0
  if (field === 'agenda') return filters.agenda !== 'ALL'
  if (field === 'urgency') return filters.urgency !== 'ALL'
  return filters[field] !== null
}

/** Los filtros validados (con las dos fechas) listos para aplicar; `null` si falta alguna fecha. Lo que no se eligió no viaja. */
export function toAppliedFilters(filters: OperatingRoomFiltersState): OperatingRoomFilterDTO | null {
  if (!filters.from || !filters.to) return null
  const applied: OperatingRoomFilterDTO = { from: isoDay(filters.from), to: isoDay(filters.to), agenda: filters.agenda, urgency: filters.urgency }
  if (filters.roomIds.length > 0) applied.roomIds = [...filters.roomIds].sort((a, b) => a - b)
  if (filters.surgeryTypeId !== null) applied.surgeryTypeId = filters.surgeryTypeId
  if (filters.surgeonId !== null) applied.surgeonId = filters.surgeonId
  if (filters.nomenclator !== null) applied.nomenclatorId = filters.nomenclator.id
  if (filters.financierId !== null) applied.financierId = filters.financierId
  if (filters.anesthesiaId !== null) applied.anesthesiaId = filters.anesthesiaId
  if (filters.agendaBlock !== null) applied.agendaId = filters.agendaBlock.id
  return applied
}

const sameList = (a: number[] | undefined, b: number[] | undefined): boolean => (a ?? []).join(',') === (b ?? []).join(',')

export const sameApplied = (a: OperatingRoomFilterDTO, b: OperatingRoomFilterDTO): boolean =>
  a.from === b.from && a.to === b.to
  && sameList(a.roomIds, b.roomIds)
  && a.surgeryTypeId === b.surgeryTypeId
  && a.surgeonId === b.surgeonId
  && a.nomenclatorId === b.nomenclatorId
  && a.financierId === b.financierId
  && a.anesthesiaId === b.anesthesiaId
  && a.agendaId === b.agendaId
  && a.agenda === b.agenda
  && a.urgency === b.urgency

/** Lo que identifica una consulta (período y filtros de la pantalla): clave del caché de las secciones que se piden aparte. */
export const filtersKey = (filters: OperatingRoomFilterDTO): string => [
  filters.from, filters.to, (filters.roomIds ?? []).join(','), filters.surgeryTypeId, filters.surgeonId,
  filters.nomenclatorId, filters.financierId, filters.anesthesiaId, filters.agendaId, filters.agenda, filters.urgency,
].join('|')

/**
 * Lo que un número, una fila o una celda pide abrir en la lista: cambios a los filtros de pantalla (visibles y quitables como
 * chips), qué cirugías se ven (realizadas, anuladas…) y, si viene del mapa de calor, la celda y su medida. Lo que no se informa se
 * deja como está.
 */
export interface OperatingRoomDrill {
  patch?: Partial<OperatingRoomSelectFilters>
  outcome?: OperatingRoomOutcome
  cell?: OperatingRoomHeatCellDTO & { measure: OperatingRoomHeatMeasure }
}
